import { Button } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Divider, Input, Modal } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { ReconciliationRow } from '../../../../features/reports/utils/reconciliationTypes'

const modalDividerStyle = { margin: 0, padding: 0 } as const

type DontPayModalProps = {
  open: boolean
  row: ReconciliationRow | null
  onClose: () => void
  onSave: (row: ReconciliationRow, reason: string) => void
  isLoading?: boolean
}

export function DontPayModal({
  open,
  row,
  onClose,
  onSave,
  isLoading = false
}: DontPayModalProps) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (open) {
      setReason('')
    }
  }, [open])

  const trimmed = reason.trim()
  const canSave = trimmed.length > 0

  return (
    <StyledModal
      wrapProps={{ 'data-testid': 'dont-pay-modal' }}
      open={open}
      onCancel={onClose}
      closable
      destroyOnClose
      width={580}
      title={
        <Stack $gap={24}>
          <ModalTitleHeading level={3}>
            {t('RECONCILIATION_DONT_PAY_CONFIRM_TITLE')}
          </ModalTitleHeading>
          <Divider style={modalDividerStyle} />
        </Stack>
      }
      footer={
        <Stack $gap={16} $mt={24}>
          <RowEnd>
            <Button onClick={onClose} disabled={isLoading}>
              {t('CANCEL')}
            </Button>
            <Button
              variant="primary"
              onClick={() => row && canSave && onSave(row, trimmed)}
              isLoading={isLoading}
              disabled={!canSave}
            >
              {t('SAVE')}
            </Button>
          </RowEnd>
        </Stack>
      }
    >
      <Stack $gap={24}>
        <ModalBody>
          <BodyText>{t('RECONCILIATION_DONT_PAY_CONFIRM_BODY')}</BodyText>
          <ReasonField>
            <ReasonLabel htmlFor="dont-pay-reason">
              <ReasonAsterisk aria-hidden>*</ReasonAsterisk>
              {t('RECONCILIATION_DONT_PAY_REASON_LABEL_TEXT')}
            </ReasonLabel>
            <Input.TextArea
              id="dont-pay-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('RECONCILIATION_DONT_PAY_REASON_PLACEHOLDER')}
              rows={2}
              disabled={isLoading}
            />
          </ReasonField>
        </ModalBody>
        <Divider style={modalDividerStyle} />
      </Stack>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 24px;
  }
  .ant-modal-header {
    padding: 0;
    margin: 0 0 12px;
    border-bottom: none;
  }
  .ant-modal-title {
    text-transform: none;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-footer {
    padding: 0;
    margin: 0;
    border-top: none;
  }
  .ant-modal-footer button:disabled {
    border: 1px solid transparent;
  }
`

const ModalTitleHeading = styled(Heading)`
  margin: 0;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const RowEnd = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const BodyText = styled(Text).attrs({ level: 1 })`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const ReasonField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ReasonLabel = styled.label`
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const ReasonAsterisk = styled.span`
  color: ${({ theme }) => theme.color.status.danger};
  flex-shrink: 0;
`

const Stack = styled.div<{ $gap?: number; $mt?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap = 8 }) => $gap}px;
  ${({ $mt }) => $mt != null && `margin-top: ${$mt}px;`}
  min-width: 0;
  width: 100%;
`
